import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";

/**
 * Searchable combobox.
 * Props:
 *  - value: string (selected value)
 *  - onChange: (value) => void
 *  - options: Array<{ value: string, label: string, keywords?: string }>
 *  - placeholder: string (button placeholder when nothing selected)
 *  - searchPlaceholder: string (input placeholder)
 *  - emptyText: string
 *  - testid: string (data-testid for trigger)
 *  - className: extra classes for trigger
 */
export default function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Pilih...",
  searchPlaceholder = "Cari...",
  emptyText = "Tidak ditemukan",
  testid,
  className,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={cn(
            "flex h-10 w-full min-w-0 items-center justify-between border border-gray-300 bg-white px-3 py-2 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-gray-900",
            !selected && "text-gray-500",
            className
          )}
        >
          <span className="truncate min-w-0 flex-1 text-left">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-none w-[var(--radix-popover-trigger-width)] min-w-[280px]"
        align="start"
      >
        <Command
          filter={(itemValue, search, keywords) => {
            const hay = (itemValue + " " + (keywords?.join(" ") || "")).toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  keywords={opt.keywords ? [opt.keywords] : undefined}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-item-${opt.value}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt.value ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
